import Decimal from 'decimal.js';
import { compact } from 'lodash';

import { AIOnboardingLifeInsurancePolicyField } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.types';
import { getHoldingOwnerEntityID } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/holdings.utils';
import { AiOnboardingModal_HouseholdFragment } from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModal.generated';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { getKgV2NodeIDFromSuggestion } from '@/modules/aiSuggestions/kgV2Resolution.utils';
import { makeUpdatePolicyInputs } from '@/modules/entities/EntitySubforms/utils/shared/policies.utils';
import {
  AugmentedCreateInsurancePolicyInput,
  InsurancePolicyKind,
} from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';
import { getPulidKind, PulidKind } from '@/utils/pulid';

// Default term duration (in years) for term life policies when not specified
const DEFAULT_TERM_LIFE_DURATION_YEARS = 20;

///////////////////////////////////////////////////////////
// SUGGESTION DATA -> FORM DATA
///////////////////////////////////////////////////////////

/**
 * Maps AI suggestions of kind LIFE_INSURANCE to form data structure for the AIOnboarding
 * modal form.
 *
 * V2 suggestions use the explicit Entity edge written by the save layer.
 *
 * @param suggestions
 * @param household
 */
export function mapLifeInsurancePoliciesToFormData(
  suggestions: AiSuggestionForReviewCard[],
  household: AiOnboardingModal_HouseholdFragment
): AIOnboardingLifeInsurancePolicyField[] {
  const entities = getNodes(household.entities);

  return compact(
    suggestions.map((s) => {
      if (!s.lifeInsurance) return null;

      // Parse death benefit amount to Decimal if it exists
      let deathBenefitAmount = null;
      if (s.lifeInsurance.deathBenefitAmount) {
        try {
          deathBenefitAmount = new Decimal(s.lifeInsurance.deathBenefitAmount);
        } catch {
          // If parsing fails, leave as null
          deathBenefitAmount = null;
        }
      }

      // Parse cash value to Decimal if it exists
      let cashValue = null;
      if (s.lifeInsurance.cashValueAmount) {
        try {
          cashValue = new Decimal(s.lifeInsurance.cashValueAmount);
        } catch {
          // If parsing fails, leave as null
          cashValue = null;
        }
      }

      // Get the insured client profile IDs, filtering out any invalid IDs
      const policyHolderIDs = compact(
        compact(s.lifeInsurance.insuredClientProfiles).map(
          (insured) => insured?.clientProfileID
        )
      ).filter((id) => getPulidKind(id) === PulidKind.ClientProfile);

      // Determine term length: use suggestion value if provided, otherwise default for term policies
      const policyType = s.lifeInsurance.policyType || '';
      const termLength =
        s.lifeInsurance.termLengthYears ||
        (policyType === InsurancePolicyKind.Term
          ? DEFAULT_TERM_LIFE_DURATION_YEARS
          : '');

      const ownerId =
        s.entity?.id ||
        getHoldingOwnerEntityID(s.lifeInsurance.owningEntityID || '', entities);

      return {
        _suggestionID: s.id,
        // Required InsurancePolicyDetails fields from defaultPolicy
        policyId: null,
        integrationInsuranceId: null,
        conversionDate: null,
        policyType,
        isConvertible: false,
        insuranceCarrier: s.lifeInsurance.insuranceCarrier || '',
        policyNumber: '',
        policyHolderIDs,
        initialPolicyHolderIDs: [],
        deathBenefitAmount,
        policyStartDate: null,
        termLength,
        premiumAmount: null,
        premiumFrequency: '',
        premiumInitialDueDate: null,
        premiumPaymentSourceID: null,
        premiumPaymentSourceKind: null,
        premiumPaymentTransferTaxKind: '',
        premiumPaymentGiftingGrantorIDs: [],
        notes: '',
        documentIds: [],
        initialDocumentIDs: [],
        initialPremiumPaymentGiftingGrantorIDs: [],
        cashValue,
        cashValueDate: null,
        loanBalanceOutstanding: null,
        // Store owner entity ID separately for grouping purposes
        ownerId,
      };
    })
  );
}

export function getEmptyLifeInsurancePolicyField(): AIOnboardingLifeInsurancePolicyField {
  return {
    _suggestionID: undefined,
    // Required InsurancePolicyDetails fields
    policyId: null,
    integrationInsuranceId: null,
    conversionDate: null,
    policyType: '',
    isConvertible: false,
    insuranceCarrier: '',
    policyNumber: '',
    policyHolderIDs: [],
    initialPolicyHolderIDs: [],
    deathBenefitAmount: null,
    policyStartDate: null,
    termLength: '',
    premiumAmount: null,
    premiumFrequency: '',
    premiumInitialDueDate: null,
    premiumPaymentSourceID: null,
    premiumPaymentSourceKind: null,
    premiumPaymentTransferTaxKind: '',
    premiumPaymentGiftingGrantorIDs: [],
    notes: '',
    documentIds: [],
    initialDocumentIDs: [],
    initialPremiumPaymentGiftingGrantorIDs: [],
    cashValue: null,
    cashValueDate: null,
    loanBalanceOutstanding: null,
    // Additional field for tracking owner entity
    ownerId: '',
  };
}

///////////////////////////////////////////////////////////
// FORM DATA -> MUTATION INPUT
///////////////////////////////////////////////////////////

/**
 * Maps the life insurance policy fields from the form data to the input structure
 * for updating entities with new insurance policies.
 *
 * @param lifeInsurancePolicies
 * @param household
 * @param suggestionsByID
 */
export function mapLifeInsurancePoliciesToInput(
  lifeInsurancePolicies: AIOnboardingLifeInsurancePolicyField[],
  household: AiOnboardingModal_HouseholdFragment,
  suggestionsByID: Record<string, AiSuggestionForReviewCard>
): AugmentedCreateInsurancePolicyInput[] {
  const policiesWithMappedInsured = lifeInsurancePolicies.map((policy) => ({
    ...policy,
  }));

  const baseInputs = makeUpdatePolicyInputs(
    {
      policies: policiesWithMappedInsured,
      initialPolicyIDs: [],
      crummeyWithdrawalPeriodDays: null,
    },
    household.id
  ).withPolicies;

  // Add kgV2NodeIDs to each policy input
  return baseInputs.map((input, index) => {
    const policy = lifeInsurancePolicies[index];
    const suggestion = policy?._suggestionID
      ? suggestionsByID[policy._suggestionID]
      : undefined;
    const kgV2NodeID = getKgV2NodeIDFromSuggestion(suggestion);

    return {
      ...input,
      create: {
        ...input.create,
        kgV2NodeIDs: kgV2NodeID ? [kgV2NodeID] : undefined,
      },
    };
  });
}
